import { Action } from "redux";
import { IssuesChecker } from "../../server/types/index.js";
import {
  GetIssuesQueryParams,
  IssueItemType,
  ThunkAction,
} from "../types/index.js";
import { IssueNotFoundError, fetch } from "../utils/utils.js";
import { showAlert } from "./Alerts.js";
import { isActionType } from "./utils.js";

const ActionTypeKeys = [
  "GET_ISSUES_START",
  "GET_ISSUES_SUCCESS",
  "GET_ISSUES_ERROR",
  "GET_ISSUES_CHECK_START",
  "GET_ISSUES_CHECK_SUCCESS",
  "GET_ISSUES_CHECK_ERROR",
  "POST_ISSUES_CHECK_START",
  "POST_ISSUES_CHECK_SUCCESS",
  "POST_ISSUES_CHECK_ERROR",
  "PATCH_ISSUE_START",
  "PATCH_ISSUE_SUCCESS",
  "PATCH_ISSUE_ERROR",
] as const;

type ActionType = (typeof ActionTypeKeys)[number];
type ActionBase<T extends ActionType> = Action<T>;

type GetIssuesStart = ActionBase<"GET_ISSUES_START"> & {
  keepExisting: boolean;
};
type GetIssuesSuccess = ActionBase<"GET_ISSUES_SUCCESS"> & {
  response: IssueItemType[];
};
type GetIssuesError = ActionBase<"GET_ISSUES_ERROR">;
type GetIssuesCheckStart = ActionBase<"GET_ISSUES_CHECK_START">;
type GetIssuesCheckSuccess = ActionBase<"GET_ISSUES_CHECK_SUCCESS"> & {
  response: IssuesChecker;
};
type GetIssuesCheckError = ActionBase<"GET_ISSUES_CHECK_ERROR">;
type PostIssuesCheckStart = ActionBase<"POST_ISSUES_CHECK_START">;
type PostIssuesCheckSuccess = ActionBase<"POST_ISSUES_CHECK_SUCCESS"> & {
  response: IssuesChecker;
};
type PostIssuesCheckError = ActionBase<"POST_ISSUES_CHECK_ERROR">;
type PatchIssueStart = ActionBase<"PATCH_ISSUE_START"> & {
  issueId: string;
  archive: boolean;
};
type PatchIssueSuccess = ActionBase<"PATCH_ISSUE_SUCCESS">;
type PatchIssueError = ActionBase<"PATCH_ISSUE_ERROR">;

export type IssuesAction =
  | GetIssuesStart
  | GetIssuesSuccess
  | GetIssuesError
  | GetIssuesCheckStart
  | GetIssuesCheckSuccess
  | GetIssuesCheckError
  | PostIssuesCheckStart
  | PostIssuesCheckSuccess
  | PostIssuesCheckError
  | PatchIssueStart
  | PatchIssueSuccess
  | PatchIssueError;

export const isIssuesAction = (action: Action): action is IssuesAction =>
  isActionType(action, ActionTypeKeys);

const getIssuesStart = (keepExisting: boolean): GetIssuesStart => ({
  type: "GET_ISSUES_START",
  keepExisting,
});

const getIssuesError = (): GetIssuesError => ({ type: "GET_ISSUES_ERROR" });

const getIssuesSuccess = (response: IssueItemType[]): GetIssuesSuccess => ({
  type: "GET_ISSUES_SUCCESS",
  response,
});

export const getIssues =
  (
    siteId: string,
    params: GetIssuesQueryParams,
    keepExisting: boolean,
  ): ThunkAction<Promise<void>> =>
  async (dispatch) => {
    dispatch(getIssuesStart(keepExisting));
    try {
      const { data } = await fetch({ dispatch }).get<IssueItemType[]>(
        `sites/${siteId}/seo/issues`,
        { params },
      );

      dispatch(getIssuesSuccess(data));
    } catch (error) {
      dispatch(getIssuesError());
      dispatch(
        showAlert(
          "Das Laden der Liste mit den Fehlern / Warnungen ist fehlgeschlagen. Versuchen Sie es später erneut!",
        ),
      );
      throw error;
    }
  };

const getIssuesCheckStart = (): GetIssuesCheckStart => ({
  type: "GET_ISSUES_CHECK_START",
});

const getIssuesCheckError = (): GetIssuesCheckError => ({
  type: "GET_ISSUES_CHECK_ERROR",
});

const getIssuesCheckSuccess = (
  response: IssuesChecker,
): GetIssuesCheckSuccess => ({ type: "GET_ISSUES_CHECK_SUCCESS", response });

export const getIssuesCheck =
  ({
    siteId,
    forceUpdate,
    showLastResult,
  }: {
    siteId: string;
    forceUpdate: boolean;
    showLastResult: boolean;
  }): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    if (getState().loadStates.issues.checker !== "unloaded" && !forceUpdate) {
      return;
    }
    dispatch(getIssuesCheckStart());
    try {
      const { data } = await fetch({ dispatch }).get<IssuesChecker>(
        `sites/${siteId}/seo/issues/check`,
        { params: { showLastResult } },
      );
      dispatch(getIssuesCheckSuccess(data));
    } catch (error) {
      dispatch(getIssuesCheckError());
      dispatch(
        showAlert(
          "Das Laden der Daten zur Problembehebung ist fehlgeschlagen. Versuchen Sie es später erneut!",
        ),
      );
      throw error;
    }
  };

const postIssueCheckStart = (): PostIssuesCheckStart => ({
  type: "POST_ISSUES_CHECK_START",
});

const postIssueCheckSuccess = (
  response: IssuesChecker,
): PostIssuesCheckSuccess => ({ type: "POST_ISSUES_CHECK_SUCCESS", response });

const postIssuesCheckError = (): PostIssuesCheckError => ({
  type: "POST_ISSUES_CHECK_ERROR",
});

export const postIssuesCheck =
  (siteId: string): ThunkAction<Promise<void>> =>
  async (dispatch) => {
    dispatch(postIssueCheckStart());
    try {
      const { data } = await fetch({ dispatch }).post<IssuesChecker>(
        `sites/${siteId}/seo/issues/check`,
      );
      dispatch(postIssueCheckSuccess(data));
    } catch (error) {
      dispatch(postIssuesCheckError());
      dispatch(
        showAlert(
          "Das Starten der Überprüfung ist fehlgeschlagen. Versuchen Sie es später erneut!",
        ),
      );
      throw error;
    }
  };

const patchIssueStart = (
  issueId: string,
  isArchived: boolean,
): PatchIssueStart => ({
  type: "PATCH_ISSUE_START",
  issueId,
  archive: isArchived,
});

const patchIssueSuccess = (): PatchIssueSuccess => ({
  type: "PATCH_ISSUE_SUCCESS",
});

const patchIssueError = (): PatchIssueError => ({ type: "PATCH_ISSUE_ERROR" });

export const patchIssue =
  (
    siteId: string,
    issueId: string,
    archive: boolean,
  ): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const currentIssue = getState().issues.issues.byId[issueId];
    if (!currentIssue) throw new IssueNotFoundError(issueId);
    const previousIsArchived = currentIssue.isArchived;
    dispatch(patchIssueStart(issueId, archive));
    try {
      await fetch({ dispatch }).patch<void>(
        `sites/${siteId}/seo/issues/${issueId}`,
        { isArchived: archive },
      );
      dispatch(patchIssueSuccess());
    } catch (error) {
      dispatch(patchIssueError());
      // Revert to previous isArchived state
      dispatch(patchIssueStart(issueId, previousIsArchived));
      dispatch(
        showAlert(
          "Das Speichern des Elements ist fehlgeschlagen. Versuchen Sie es später erneut!",
        ),
      );
      throw error;
    }
  };
